import { Glue42Core } from "@glue42/core";

import { Glue42Office } from "../../../glue4office";
import { convertToT42Email, getOlDefaultFolders } from "../helpers/index";
import { CreateLocalEmailParams, OlDefaultFolders, T42ValueEmail } from "../types";
import { EmailImpl } from "../items/EmailImplementation";
import { CreateLocalEmailMethodName } from "../helpers/methodNames";
import { validateLocalEmailParameters, validateLocation } from "../helpers/validations";

const createLocalEmail = (ids: Glue42Office.Outlook.T42Id, emailParams: Glue42Office.Outlook.LocalEmailParams) => {
    const localEmail: any = { ...convertToT42Email(emailParams), ids };
    Object.keys(localEmail).forEach((key) => {
        if (!localEmail[key]) {
            delete localEmail[key];
        }
    });
    return localEmail;
};

export const createLocalEmailMethod = (agm: Glue42Core.AGM.API, localEmailParams: Glue42Office.Outlook.LocalEmailParams): Promise<Glue42Office.Outlook.Email> =>
    new Promise((resolve, reject) => {
        validateLocalEmailParameters(agm, localEmailParams);
        const successHandler = (res: { returned: { localEmailIds: any } }, emailParams: Glue42Office.Outlook.LocalEmailParams) => {
            const localEmail = new EmailImpl(createLocalEmail(res.returned.localEmailIds, emailParams), agm);
            resolve(localEmail);
        };

        const errorHandler = (err: any) => {
            reject(err.message);
            return;
        };

        if (localEmailParams.location) {
            validateLocation(localEmailParams.location);
        }

        return agm.invoke(CreateLocalEmailMethodName, getParams(localEmailParams))
            .then((res) => successHandler(res, localEmailParams))
            .catch((err) => errorHandler(err));
    });

const getT42Email = (localEmailParams?: Glue42Office.Outlook.LocalEmailParams): Glue42Office.Outlook.EmailParams => {
    return Object.keys(localEmailParams).reduce<Glue42Office.Outlook.EmailParams>((obj: Glue42Office.Outlook.EmailParams, key: string) => {
        if (key === "additionalProps") {
            return obj;
        }
        obj[key] = localEmailParams[key];
        return obj;
    }, {});
};

const getLocation = (location: Glue42Office.Outlook.Email | Glue42Office.Outlook.CommonOutlookFolder | string) => {
    const defaultFolders: OlDefaultFolders = getOlDefaultFolders();

    if (!location) {
        return { defaultFolderIndex: defaultFolders.$Inbox };
    }

    const emailIds = (location as Glue42Office.Outlook.Email).ids;
    if (emailIds && emailIds[0].nativeId && emailIds[0].systemName) {
        return { parentItemIds: emailIds };
    }

    if (typeof location === "string") {
        if (location.indexOf("$") < 0) {
            return { folderPath: location };
        }

        if (typeof defaultFolders[location] === "number") {
            return { defaultFolderIndex: defaultFolders[location] };
        }
    }

    return { defaultFolderIndex: defaultFolders.$Inbox };
};

const getParams = (localEmailParams?: Glue42Office.Outlook.LocalEmailParams) => {
    const params: CreateLocalEmailParams = {
        location: getLocation(localEmailParams.location),
        email: convertToT42Email(getT42Email(localEmailParams)) as T42ValueEmail,
    };

    if (localEmailParams.additionalProps) {
        params.additionalProps = localEmailParams.additionalProps;
    }

    return params;
};
